<template>
    <ElevatedCard :widthType="WidthType.third" class="container-widget">
        <HeaderWidget :iconUrl="loadIcon" :title="widgetTitle" @click="navigationTo" />
        <div v-if="widget" class="regles-widget-group">
            <ReglePastille cle="dashboard.widgetPerimetreCommun" />
            <ReglePastille cle="dashboard.widgetDevisFenetreFixe" />
        </div>
        <div v-if="widget" class="site-widget-container">
            <span v-if="widget.sites == null || widget.sites.length > 1" class="site-widget">{{
                    $t('multisites')
                }}</span>
            <div v-for="site in widget.sites" v-else :key="site.id" class="site-widget-item">
                <div class="site-widget">{{ site.description }}</div>
            </div>
        </div>
        <NoData
            v-if="!isLoading && !getEstimateData.nbPublies && !getEstimateData.nbEnCours && !getEstimateData.nbRealises"
            class="no-data"
            textNoData="widget.estimate.noData"
        />
        <div v-else class="container-tiles">
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('estimateWidget.toValidate')"
                :titleNotBold="$tc('estimateWidget.estimate')"
                :value="getEstimateData.nbPublies"
                class="tile"
                color="--a-valider"
                @click="filteredNavigation(EtatDevis.publie)"
            />
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('estimateWidget.awaitingRealization')"
                :titleNotBold="$t('estimateWidget.estimate')"
                :value="getEstimateData.nbEnCours"
                class="tile"
                color="--en-cours"
                @click="filteredNavigation(EtatDevis.enCours)"
            />
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('estimateWidget.inProgress', getEstimateData.nbRealises)"
                :titleNotBold="$t('estimateWidget.estimate')"
                :value="getEstimateData.nbRealises"
                class="tile"
                color="--fait"
                @click="filteredNavigation(EtatDevis.realise)"
            />
        </div>
    </ElevatedCard>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ElevatedCard, { WidthType } from '@/components/ui-components/ElevatedCard.vue';
import HeaderWidget from '@/components/ui-components/HeaderWidget.vue';
import Tile from '@/components/ui-components/Tile.vue';
import HomeService from '../services/HomeService';
import NoData from '@/components/ui-components/NoDataWidget.vue';
import { DevisActions } from '@/modules/Devis/store/action-types';
import { EtatDevis } from '@/models/Devis';
import { Widget } from '@/models/Widget';
import EstimateWidgetItems from '@/models/EstimateWidgetItems';
import { EquipementAction } from '@/modules/Equipements/store/actions-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'Estimate',
    components: { ElevatedCard, HeaderWidget, Tile, NoData, ReglePastille },
    props: {
        widget: { type: Object as PropType<Widget>, required: false },
        equipementDevisData: { type: Object as PropType<EstimateWidgetItems>, required: false },
        equipementDevisLoading: { type: Boolean, default: false },
        isBim: { type: Boolean, default: false },
        title: { type: String, required: false },
    },
    setup() {
        return {
            WidthType,
            EtatDevis,
        };
    },
    data() {
        return {
            widgetItems: { nbPublies: 0, nbEnCommande: 0, nbRealises: 0, nbEnCours: 0 } as EstimateWidgetItems,
            widgetLoading: false,
            widgetError: false,
        };
    },
    async mounted() {
        if (this.equipementDevisData) {
            return;
        }
        const sites = this.widget?.sites ?? [];
        this.widgetLoading = true;
        try {
            const response = await HomeService.getEstimateData({
                siteIds: sites.map(site => site.id),
                chantierIds: sites.map(site => site.chantierId),
                dateDebut: this.$store.state.homeState.widgetDevis.dateDebut,
                dateFin: this.$store.state.homeState.widgetDevis.dateFin,
            });
            this.widgetItems = response.data;
        } catch (e) {
            this.widgetError = true;
        } finally {
            this.widgetLoading = false;
        }
    },
    methods: {
        navigationTo() {
            if (this.isBim) {
                this.$store.dispatch(`equipementState/${EquipementAction.DEVIS_NAV_FILTER}`, []);
                return;
            }
            this.$store.dispatch(DevisActions.NAV_FILTER, { status: [], sites: this.widget?.sites ?? [] });
            this.$router.push('/suivi-devis');
        },
        filteredNavigation(value: EtatDevis) {
            if (this.isBim) {
                this.$store.dispatch(`equipementState/${EquipementAction.DEVIS_NAV_FILTER}`, value ? [value] : []);
                return;
            }
            this.$store.dispatch(DevisActions.NAV_FILTER, { status: value ? [value] : [], sites: this.widget?.sites ?? [] });
            this.$router.push('/suivi-devis');
        },
    },
    computed: {
        getEstimateData() {
            return this.equipementDevisData || this.widgetItems;
        },
        isLoading() {
            return this.widget ? this.widgetLoading : this.equipementDevisLoading;
        },
        isError() {
            return this.widget ? this.widgetError : false;
        },
        loadIcon() {
            return require('@/assets/img/menu/calculate_45.png');
        },
        widgetTitle() {
            return this.title || (this.widget ? this.widget.libelle : '');
        },
    },
});
</script>

<style scoped>
.regles-widget-group {
    display: flex;
    align-items: center;
    gap: 16px;
}
.container-tiles {
    display: flex;
    justify-content: space-around;
    flex-direction: row;
    flex-grow: 1;
    gap: 25px;
    align-items: center;
    width: 86%;
    margin-left: auto;
    margin-right: auto;
    padding: 10px 10px 8%;
}
@media screen and (max-width: 1600px) {
    .container-tiles {
        gap: 10px;
        width: 95%;
    }
}
.container-widget {
    display: flex;
    flex-direction: column;
    background-color: var(--white);
    width: 32%;
}
.site-widget-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.site-contract {
    font-size: 11px;
    color: var(--dark-grey);
}
.tile {
    margin: 5px 0;
    cursor: pointer;
    height: 125px;
    max-height: 20vh;
}
</style>
